import Box from "@/components/Box";
import Loading from "@/components/Loading";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { ORDER_RETEST_STATUS } from "@/core/constants";
import { theme } from "@/core/theme";
import { ORGANISATION_ORDER_DETAIL_QUERY } from "@/graphql/organisations/queries/orders";
import useOrderConfirmation from "@/hooks/use-order-confirmation";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Currency from "@/tpo/Currency";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import { useQuery } from "@apollo/client";

import OrderListItem from "../ui/OrderListItem";
import OrderedBy from "./OrderedBy";
import OrganisationOrderConfirmationBox from "./OrganisationOrderConfirmationBox";
import OrganisationOrderSummary from "./OrganisationOrderSummary";

export default function OrganisationOrderDetails({ id, isModal = true }) {
  const { organisation } = useOrganisationContext();

  const { data, loading } = useQuery(ORGANISATION_ORDER_DETAIL_QUERY, {
    variables: {
      organisation: parseInt(organisation?.id),
      id
    },
    skip: !organisation?.id
  });

  const order = data?.organisationOrder;
  const basket = data?.organisationOrder?.sourceBasket;

  const { showConfirmation, requiresPhlebotomy } = useOrderConfirmation(order, isModal);

  const totalPrices = {
    discounted: basket?.basketTotalDiscount || 0,
    refunded: basket?.refunded || 0,
    shipping: basket?.basketShippingCost || 0, // basketShippingCost graphql field points to `shipping_cost` property on basket model
    total: basket?.basketTotalPrice || 0,
    commission: basket?.basketTotalCommission || 0
  };

  if (basket?.refunded) {
    totalPrices.total = totalPrices.total - basket.refunded;
    totalPrices.total = Math.max(0, totalPrices.total);
    if (basket.basketTotalPrice > 0) {
      let ratioRefunded = basket.refunded / basket.basketTotalPrice;
      totalPrices.commission = totalPrices.commission - totalPrices.commission * ratioRefunded;
    }
  }

  if (loading || !order) {
    return <Loading />;
  }

  // Bundle base lines (products with required add-ons); their component add-on
  // lines reference these via `parent`.
  const bundleLineItemIds = new Set(
    (order.testItems || [])
      .filter(item => item.product?.requiredAddons?.length)
      .map(item => item.id)
  );

  let orderedByUi = order ? <OrderedBy order={order} /> : null;

  return (
    <Stack
      maxWidth={1538}
      mx="auto"
      pt={isModal ? 0 : theme.spacing.section.pt}
      pb={isModal ? 0 : theme.spacing.section.pb}
      px={isModal ? 0 : [20, 20, "5.5vw"]}
      gap={[20, 20, 40]}
    >
      {showConfirmation && (
        <OrganisationOrderConfirmationBox requiresPhlebotomy={requiresPhlebotomy} />
      )}

      <Box
        bg="white"
        borderRadius={5}
        maxWidth={1280}
        px={isModal ? 0 : [20, 20, 40]}
        pt={isModal ? 0 : theme.spacing.section.pt}
        pb={isModal ? 0 : theme.spacing.section.pb}
      >
        <Box maxWidth={1020} mx="auto">
          <PanelBoxV2
            maxWidth={860}
            outer={{
              px: 20,
              pb: [20, 20, 40]
            }}
          >
            {orderedByUi}
          </PanelBoxV2>
          <PanelBoxV2
            maxWidth={860}
            outer={{
              bg: "haze",
              py: 20,
              px: 20
            }}
          >
            <Box py={20}>
              <OrganisationOrderSummary order={order} />
            </Box>
            <Stack py={20}>
              <Box fontFamily="gilroyBold" fontSize={16}>
                Shipping details
              </Box>
              <Stack fontFamily="gilroyMedium" fontSize={14}>
                {!!order?.shippingFirstName && (
                  <span
                    fontFamily="inherit"
                    fontSize="inherit"
                    style={{
                      wordBreak: "break-word",
                      wordWrap: "anywhere"
                    }}
                  >
                    {order?.shippingFirstName}
                  </span>
                )}
                {!!order?.shippingAddress1 && (
                  <span
                    fontFamily="inherit"
                    fontSize="inherit"
                    style={{
                      wordBreak: "break-word",
                      wordWrap: "anywhere"
                    }}
                  >
                    {order?.shippingAddress1}
                  </span>
                )}
                {!!order?.shippingAddress2 && (
                  <span
                    fontFamily="inherit"
                    fontSize="inherit"
                    style={{
                      wordBreak: "break-word",
                      wordWrap: "anywhere"
                    }}
                  >
                    {order?.shippingAddress2}
                  </span>
                )}
                {!!order?.shippingTownCity && (
                  <span
                    fontFamily="inherit"
                    fontSize="inherit"
                    style={{
                      wordBreak: "break-word",
                      wordWrap: "anywhere"
                    }}
                  >
                    {order?.shippingTownCity}
                  </span>
                )}
                {!!order?.shippingPostalCode && (
                  <span
                    fontFamily="inherit"
                    fontSize="inherit"
                    style={{
                      wordBreak: "break-word",
                      wordWrap: "anywhere"
                    }}
                  >
                    {order?.shippingPostalCode}
                  </span>
                )}
                {!!order?.shippingPhone && (
                  <span
                    fontFamily="inherit"
                    fontSize="inherit"
                    style={{
                      wordBreak: "break-word",
                      wordWrap: "anywhere"
                    }}
                  >
                    {order?.shippingPhone}
                  </span>
                )}
                {!!order?.shippingCountry && (
                  <span
                    fontFamily="inherit"
                    fontSize="inherit"
                    style={{
                      wordBreak: "break-word",
                      wordWrap: "anywhere"
                    }}
                  >
                    {order?.shippingCountry}
                  </span>
                )}
              </Stack>
            </Stack>
          </PanelBoxV2>
          {basket && !loading && (
            <PanelBoxV2
              maxWidth={860}
              outer={{
                pt: [30, 30, 60],
                pb: [60, 60, 120],
                px: 20
              }}
            >
              <Stack gap={20}>
                {order.testItems.map(lineItem => {
                  // Component arrays of a Cyrex bundle: the bundle base line carries the
                  // price/status, so its add-on components show neither price nor badges.
                  const isBundleAddon =
                    !!lineItem.parent &&
                    bundleLineItemIds.has(lineItem.parent.id);
                  return (
                    <OrderListItem
                      order={order}
                      orderTestItem={lineItem}
                      name={lineItem.nameInBasket}
                      registrationId={lineItem.providerTestId}
                      price={lineItem.price}
                      clinicLocation={lineItem.clinicLocation}
                      clinicBooking={lineItem.clinicBooking}
                      key={lineItem.id}
                      isAddOn={!!lineItem.parent}
                      currencySymbol={basket?.currencySymbol}
                      supppressPrice={order.status == ORDER_RETEST_STATUS || isBundleAddon}
                      showBadges={!isBundleAddon}
                      registeredUserFullName={lineItem.registeredUserFullName}
                    />
                  );
                })}
                {order.supplementItems.map(lineItem => (
                  <OrderListItem
                    order={order}
                    orderTestItem={lineItem}
                    name={lineItem.nameInBasket}
                    price={lineItem.price}
                    key={lineItem.id}
                    currencySymbol={basket?.currencySymbol}
                    supppressPrice={order.status == ORDER_RETEST_STATUS}
                    showBadges={false}
                  />
                ))}
              </Stack>
              <Spacer py={20} />
              {order.status != ORDER_RETEST_STATUS && (
                <Stack gap={10} alignItems="flex-end">
                  {totalPrices.discounted > 0 && (
                    <Currency
                      symbol={basket?.currencySymbol}
                      value={totalPrices.discounted * -1}
                      prefix={"DISCOUNT"}
                      fontSize={28}
                    />
                  )}
                  {totalPrices.refunded > 0 && (
                    <Currency
                      symbol={basket?.currencySymbol}
                      value={totalPrices.refunded * -1}
                      color="red"
                      prefix={"REFUNDED"}
                      fontSize={28}
                    />
                  )}
                  <Currency
                    prefix="SHIPPING"
                    value={totalPrices.shipping}
                    fontSize={28}
                    symbol={basket?.currencySymbol}
                  />
                  <Currency
                    prefix="TOTAL"
                    value={totalPrices.total}
                    fontSize={44}
                    symbol={basket?.currencySymbol}
                  />
                  {basket.payee === "patient" && totalPrices.commission > 0 && (
                    <Currency
                      prefix="COMMISSION"
                      value={totalPrices.commission}
                      fontSize={28}
                      symbol={basket?.currencySymbol}
                    />
                  )}
                </Stack>
              )}
            </PanelBoxV2>
          )}
        </Box>
      </Box>
    </Stack>
  );
}
